import React, { useEffect, useMemo, useState, useRef, ChangeEvent, KeyboardEvent } from "react";
import { useLocation } from "react-router-dom";
import { toast } from "react-toastify";
import Select, { SingleValue, components } from "react-select";
import { AsyncPaginate, LoadOptions } from "react-select-async-paginate";
import DataTable from "components/DataTable";
import PaginationControls from "components/PaginationControls";
import {
    useReactTable,
    getCoreRowModel,
    getFilteredRowModel,
    getPaginationRowModel,
    flexRender,
    createColumnHelper,
} from "@tanstack/react-table";

import { Table, TableHead, TableRow, TableCell, TableBody } from '@mui/material';
import TablePaginationControls from "components/TableandmobilePaginationcontrol"
import { DesktopTableParts, MobileCardsOnly } from "components/TableandMobilecomponent";
import StatCard from "./ReportsCards";

import { getAuthCookie } from "../utils/authCookies";
import { fetchMarketplaceProfilesSelect2 } from "api/postTypeApiCalls";

const DATE_OPTIONS = [
    { label: "Last 7 Days", value: "last_7_days" },
    { label: "Last 15 Days", value: "last_15_days" },
    { label: "Last 30 Days", value: "last_30_days" },
    { label: "Custom", value: "custom" },
];

const PAYMENT_METHOD_OPTIONS = [
    { label: "Credit Card", value: "Credit Card" },
    { label: "Net 30", value: "Net 30" },
    { label: "Bank Transfer", value: "Bank Transfer" },
];

const PAYMENT_STATUS_OPTIONS = [
    { label: "All", value: "all" },
    { label: "Paid", value: "Paid" },
    { label: "Partial Payment", value: "Partial Payment" },
    { label: "Pending", value: "Pending" },
    { label: "Overdue", value: "Overdue" },
];


export interface BrokerageSalesOrderRow {
    agent_name: string;
    office_name: string;
    installations?: number; // For installation summary report
    sales_orders: number;
    payment_method: string;
    total_billed: number;
    paid: number;
    balance: number;
    over_paid: boolean;
}

type SelectOption = {
    label: string;
    value: string;
};

type CardsSummary = {
    total_orders: number;
    total_revenue: number;
    amount_paid: number;
    outstanding_balance: number;
    total_installations: number; // For installation summary report
    total_billed: number; // For installation summary report
    installations_completed: number; // For installation summary report
    percent_paid: number; // For installation summary report
    percent_pending: number; // For installation summary report
    avg_order_value : number; // For sales order summary report
};


const DropdownIndicator = (props: any) => {
    if (props.selectProps.isDisabled) return null;
    return (
        <components.DropdownIndicator {...props}>
            <i className="ri-arrow-down-s-line fs-5" style={{ color: "#6c757d" }} />
        </components.DropdownIndicator>
    );
};

const customSelectStyles = {
    /* hide all indicators when disabled */
    indicatorsContainer: (provided: any, state: any) => ({
        ...provided,
        display: state.isDisabled ? "none" : "flex",
    }),
    dropdownIndicator: (provided: any, state: any) => ({
        ...provided,
        display: state.isDisabled ? "none" : "flex",
    }),
    indicatorSeparator: () => ({ display: "none" }),

    /* keep bg and cursor right; do NOT fade */
    control: (provided: any, state: any) => ({
        ...provided,
        backgroundColor: state.isDisabled ? "#f2f2f2" : provided.backgroundColor,
        cursor: state.isDisabled ? "not-allowed" : "pointer",
        opacity: 1,
    }),

    /* ——— make text BLACK when disabled ——— */
    singleValue: (provided: any, state: any) => ({
        ...provided,
        color: state.isDisabled ? "#000" : provided.color,
    }),
    valueContainer: (provided: any, state: any) => ({
        ...provided,
        color: state.isDisabled ? "#000" : provided.color,
    }),
    input: (provided: any, state: any) => ({
        ...provided,
        color: state.isDisabled ? "#000" : provided.color,
        WebkitTextFillColor: state.isDisabled ? "#000" : undefined, // Safari
    }),
    placeholder: (provided: any, state: any) => ({
        ...provided,
        /* if field has no value, keep placeholder readable too */
        color: state.isDisabled ? "#000" : provided.color,
    }),

    /* if you ever use isMulti, keep chips readable */
    multiValue: (p: any, s: any) => ({
        ...p,
        backgroundColor: s.isDisabled ? "#e5e7eb" : p.backgroundColor,
    }),
    multiValueLabel: (p: any, s: any) => ({
        ...p,
        color: s.isDisabled ? "#000" : p.color,
    }),
    multiValueRemove: (p: any, s: any) => ({
        ...p,
        display: s.isDisabled ? "none" : p.display,
    }),
};



export default function BrokerageSalesOrderBillingReport({ report }: { report: string }) {
    const userId = getAuthCookie("userId");
    const token = getAuthCookie("sessionToken");
    const [data, setData] = useState<BrokerageSalesOrderRow[]>([]);
    const [cards, setCards] = useState<CardsSummary | null>(null);
    const [datePreset, setDatePreset] = useState(DATE_OPTIONS[2]);
    const [customFrom, setCustomFrom] = useState("");
    const [customTo, setCustomTo] = useState("");
    const [selectedOffice, setSelectedOffice] = useState<SelectOption | null>(null);
    const [selectedAgent, setSelectedAgent] = useState<SelectOption | null>(null);
    const [supplier, setSupplier] = useState<any>(null);
    const [paymentStatus, setPaymentStatus] = useState(PAYMENT_STATUS_OPTIONS[0]); // All
    const [paymentMethod, setPaymentMethod] = useState<any[]>([]);
    const [pagination, setPagination] = useState({ pageIndex: 0, pageSize: 10 });
    const [rowCount, setRowCount] = useState(0);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string>("");
    const [generateClicked, setGenerateClicked] = useState(false);
    const [filtersOpen, setFiltersOpen] = useState(false);
    const [globalFilter, setGlobalFilter] = useState("");
    const [searchInput, setSearchInput] = useState<string>(globalFilter ?? "")
    const topRef = useRef<HTMLDivElement>(null);
    
    // Calculate date range limits (last 6 months from today)
    const dateRangeLimits = useMemo(() => {
        const today = new Date();
        const sixMonthsAgo = new Date();
        sixMonthsAgo.setMonth(today.getMonth() - 6);
        
        // Format as YYYY-MM-DD for date input
        const formatDate = (date: Date) => {
            const year = date.getFullYear();
            const month = String(date.getMonth() + 1).padStart(2, '0');
            const day = String(date.getDate()).padStart(2, '0');
            return `${year}-${month}-${day}`;
        };
        
        return {
            minDate: formatDate(sixMonthsAgo),
            maxDate: formatDate(today)
        };
    }, []);

    // Validate and reset dates when custom is selected or dates are out of range
    useEffect(() => {
        if (datePreset.value === "custom") {
            const minDate = dateRangeLimits.minDate;
            const maxDate = dateRangeLimits.maxDate;
            
            // Reset From date if it's outside the valid range
            if (customFrom && (customFrom < minDate || customFrom > maxDate)) {
                setCustomFrom("");
            }
            
            // Reset To date if it's outside the valid range
            if (customTo && (customTo < minDate || customTo > maxDate)) {
                setCustomTo("");
            }
        }
    }, [datePreset.value, dateRangeLimits, customFrom, customTo]);

    // Dropdown options
    const [brokerageOptions, setBrokerageOptions] = useState([]);
    const [officeOptions, setOfficeOptions] = useState([]);
    const [agentOptions, setAgentOptions] = useState([]);
    const [supplierOptions, setSupplierOptions] = useState([]);

    const location = useLocation();
    const hasShownToast = useRef(false);

    // Detect login type
    const isTeammember = location.pathname.includes('/teammember');
    const isRealtor = location.pathname.includes('/realtor');

    // State for parent brokerage firm (for teammember login)
    const [parentBrokerageId, setParentBrokerageId] = useState<string | null>(null);
    const [parentBrokerageName, setParentBrokerageName] = useState<string | null>(null);

    useEffect(() => {
        if (location.state?.successMessage && !hasShownToast.current) {
            toast.success(location.state.successMessage);
            hasShownToast.current = true;
            window.history.replaceState({}, document.title);
        }
    }, [location.state]);

    // Fetch parent hierarchy for teammember login
    const fetchParentHierarchy = async () => {
        if (!isTeammember || !token) return;
        
        try {
            const response = await fetch(
                `${process.env.REACT_APP_API_URL}/parent-hierarchy`,
                {
                    method: "GET",
                    headers: {
                        Authorization: `Bearer ${token}`,
                        "Content-Type": "application/json",
                    },
                }
            );

            if (response.ok) {
                const data = await response.json();
                
                // Find the parent with license_type "BROKERAGE_FIRM"
                const brokerageFirm = data.parent_chain?.find(
                    (parent: any) => parent.license_type === "BROKERAGE_FIRM"
                );
                
                if (brokerageFirm) {
                    setParentBrokerageId(brokerageFirm.id || brokerageFirm._id);
                    setParentBrokerageName(brokerageFirm.name);
                }
            }
        } catch (error) {
            console.error("Error fetching parent hierarchy:", error);
        }
    };

    // Fetch parent hierarchy for teammember login
    useEffect(() => {
        if (isTeammember && token) {
            fetchParentHierarchy();
        }
    }, [isTeammember, token]);

    // Fetch dropdown options for brokerage, office, agent (replace with your APIs)
    // useEffect(() => {
    //     // Fetch brokerageOptions
    //     const fetchBrokerageFirms = async () => {
    //         try {
    //             const response = await fetch(
    //                 `${process.env.REACT_APP_API_URL}/users-search?created_by.id=${userId}&licenseType=BROKERAGE_FIRM`,
    //                 {
    //                     method: "GET",
    //                     headers: {
    //                         Authorization: `Bearer ${token}`,
    //                         "Content-Type": "application/json",
    //                     },
    //                 }
    //             );

    //             const data = await response.json();
    //             // console.log("fetchBrokerageFirms data :", data);

    //             if (!response.ok) {
    //                 // throw new Error("Failed to fetch agents");
    //                 toast.error(data.error || "Failed to fetch firms");
    //             }

    //             setBrokerageOptions(data.map((i: any) => ({ label: `${i.firstName} ${i.lastName}`, value: i.id })));
    //             // console.log("Brokerage Firms:", data);
    //         } catch (err) {
    //             setError(
    //                 err instanceof Error ? err.message : "An unknown error occurred"
    //             );
    //             console.error("Error fetching brokerage firms:", err);
    //         } finally {
    //             setLoading(false);
    //         }
    //     };
    //     fetchBrokerageFirms()
    // }, []);

    useEffect(() => {
        const fetchBrokerageOffice = async () => {
            // For teammember, wait for parentBrokerageId to be available
            if (isTeammember && !parentBrokerageId) {
                return;
            }

            try {
                // Use parentBrokerageId for teammember, userId for realtor
                const effectiveUserId = (isTeammember && parentBrokerageId) ? parentBrokerageId : userId;

                const response = await fetch(
                    `${process.env.REACT_APP_API_URL}/office/${effectiveUserId}`,
                    {
                        method: "GET",
                        headers: {
                            Authorization: `Bearer ${token}`,
                            "Content-Type": "application/json",
                        },
                    }
                );

                const data = await response.json();
                // console.log("fetchBrokerageFirms data :", data);

                if (!response.ok) {
                    // throw new Error("Failed to fetch agents");
                    toast.error(data.error || "Failed to fetch offices");
                }
                setOfficeOptions(data?.map((i: any) => ({ label: i.officeName, value: i._id })));
                // console.log("Office based on the selected brokerage:", data);
            } catch (err) {
                setError(
                    err instanceof Error ? err.message : "An unknown error occurred"
                );
                console.error("Error fetching brokerage firms:", err);
            } finally {
                // setLoading(false);
            }
        };
        fetchBrokerageOffice();
    }, [isTeammember, parentBrokerageId, userId, token]);

    // brokerage id for API calls
    const brokerageId = (isTeammember && parentBrokerageId ? parentBrokerageId : userId) || "";

    // Load agent options (select2 style). If an office is selected, return office agents,
    // otherwise return brokerage agents via users-search (licenseType=BROKERAGE_AGENT).
    const PAGE_SIZE = 20;
    const loadAgentOptions: LoadOptions<{ value: string; label: string }, any, { page: number }> = async (
        inputValue,
        _loadedOptions,
        additional
    ) => {
        const page = additional?.page ?? 1;
        try {
            if (selectedOffice?.value) {
                // fetch office agents
                const res = await fetch(`${process.env.REACT_APP_API_URL}/agent/office/${selectedOffice.value}`, {
                    headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
                });
                if (!res.ok) return { options: [], hasMore: false, additional: { page } };
                const data = await res.json();
                const options = (Array.isArray(data) ? data : data.data || []).map((a: any) => ({
                    value: a.id || a._id,
                    label: `${a.firstName || ""} ${a.lastName || ""}`.trim() || a.email || String(a.id || a._id),
                }));
                return { options, hasMore: false, additional: { page: page + 1 } };
            }

            // brokerage agents via users-search
            const params = new URLSearchParams({
                licenseType: "BROKERAGE_AGENT",
                brokerageFirmID: brokerageId,
                page: String(page),
                limit: String(PAGE_SIZE),
            });
            const q = (inputValue || "").trim();
            if (q) params.set("search", q);

            const url = `${process.env.REACT_APP_API_URL}/users-search?${params.toString()}`;
            const response = await fetch(url, {
                headers: {
                    Accept: "application/json",
                    Authorization: `Bearer ${token}`,
                    "Content-Type": "application/json",
                },
            });
            if (!response.ok) return { options: [], hasMore: false, additional: { page } };
            const json = await response.json();
            const results = Array.isArray(json) ? json : json.results || [];
            const hasMore = Array.isArray(json) ? results.length === PAGE_SIZE : json.pagination?.more || false;
            const options = results.map((u: any) => ({
                value: String(u.id ?? u._id ?? ""),
                label: `${u.firstName || ""} ${u.lastName || ""}`.trim() || u.email || String(u.id ?? u._id ?? ""),
            }));
            return { options, hasMore, additional: { page: page + 1 } };
        } catch (err) {
            console.error("Failed to load agent options:", err);
            return { options: [], hasMore: false, additional: { page } };
        }
    };

    useEffect(() => {
        const fetchSuppliersByUserId = async () => {
            if (!token) return;
            try {
                const { results } = await fetchMarketplaceProfilesSelect2(token, {
                    category: "signage",
                    page: 1,
                    limit: 20,
                });
                // `signage_id` in the report payload expects the vendor (signage) id.
                setSupplierOptions(
                    (results || [])
                        .map((r: any) => ({
                            label: String(r.text || "").trim(),
                            value: String(r.vendor_id || r.id || "").trim(),
                        }))
                        .filter((o: any) => o.label && o.value) as any
                );
            } catch (err) {
                setError(
                    err instanceof Error ? err.message : "An unknown error occurred"
                );
            } finally {
                setLoading(false);
            }
        };
        fetchSuppliersByUserId();
    }, [token]);

    useEffect(() => {
        if (selectedOffice) {
            const fetchOfficeAgents = async () => {
                try {
                    const response = await fetch(
                        `${process.env.REACT_APP_API_URL}/agent/office/${selectedOffice.value}`,
                        {
                            method: "GET",
                            headers: {
                                Authorization: `Bearer ${token}`,
                                "Content-Type": "application/json",
                            },
                        }
                    );
                    const data = await response.json();
                    // console.log("officeAgents :", data);

                    if (!response.ok) {
                        toast.error(data.error || "Failed to fetch agents");
                    } else {
                        setAgentOptions(data?.map((i: any) => ({ label: `${i.firstName} ${i.lastName}`, value: i.id })));
                        // console.log("Office Agents:", data);
                    }
                } catch (err) {
                    setError(
                        err instanceof Error ? err.message : "An unknown error occurred"
                    );
                } finally {
                    setLoading(false);
                }
            };
            fetchOfficeAgents();
        } else {
            setAgentOptions([]);
            setSelectedAgent(null);
        }
    }, [selectedOffice]);




    // useEffect(() => {
    //     fetchData();
    //     // eslint-disable-next-line
    // }, [
    //     datePreset,
    //     customFrom,
    //     customTo,
    //     selectedOffice,
    //     selectedAgent,
    //     supplier,
    //     paymentMethod,
    //     globalFilter,
    //     pagination,
    // ]);

    // Fetch data when Generate Button is clicked
    useEffect(() => {
        if (generateClicked) {
            fetchData();
        }
        else {
            fetchData();
        }
        // eslint-disable-next-line
    }, [generateClicked, pagination, globalFilter, report, location.pathname]);

    useEffect(() => {
        setPagination((prev) =>
            prev.pageIndex === 0 ? prev : { ...prev, pageIndex: 0 }
        );
    }, [report, location.pathname]);

    useEffect(() => {
        setFiltersOpen(false);
        clearFilters();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [location.pathname]);

    function handleGenerate() {
        setPagination((prev) => ({ ...prev, pageIndex: 0 }));
        setGenerateClicked(true);
    }

    function clearFilters() {
        setDatePreset(DATE_OPTIONS[2]); // Last 30 Days
        setCustomFrom("");
        setCustomTo("");
        setSelectedOffice(null);
        setSelectedAgent(null);
        setSupplier(null);
        setPaymentStatus(PAYMENT_STATUS_OPTIONS[0]); // All
        setPaymentMethod([]);
        setSearchInput("");
        setGlobalFilter("");
        setPagination({ pageIndex: 0, pageSize: 10 });
        setGenerateClicked(true);
    }

    async function fetchData() {
        setLoading(true);
        const endpoint = report === "installation_summary" ? "reports/brokerage/installation_billing_summary" : "reports/brokerage/sales_order_billing_summary";
        const res = await fetch(
            `${process.env.REACT_APP_API_URL}/${endpoint}`,
            {
                method: "POST",
                headers: {
                    Authorization: `Bearer ${token}`,
                    "Content-Type": "application/json",
                },
                body: JSON.stringify({
                    page: pagination.pageIndex + 1,
                    limit: pagination.pageSize,
                    office_id: selectedOffice?.value,
                    agent_id: selectedAgent?.value,
                    signage_id: supplier?.value,
                    payment_status: paymentStatus.value === "all" ? [] : [paymentStatus.value],
                    // payment_method: paymentMethod.map((x) => x.value),
                    search: globalFilter,
                    date_range: {
                        type: datePreset.value,
                        from: datePreset.value === "custom" ? customFrom : "",
                        to: datePreset.value === "custom" ? customTo : "",
                    },
                }),
            }
        );
        const result = await res.json();
        setData(result.data || []);
        setCards(result.cards || []);
        setRowCount(result.total || 0);
        setLoading(false);
    }

    const columnHelper = createColumnHelper<BrokerageSalesOrderRow>();

    const columns = useMemo(() => [
        columnHelper.display({
            id: "sno",
            header: () => <div className="th-text S-No">S.No.</div>,
            cell: (info) => (
                <div className="text-sm S-No">
                    {pagination.pageIndex * pagination.pageSize + info.row.index + 1}
                </div>
            ),
        }),
        columnHelper.accessor("agent_name", {
            header: () => <div className="th-text">Agent Name</div>,
            cell: (info) => (
                <div className="fw-medium text-sm">{info.getValue() || "--"}</div>
            ),
        }),
        columnHelper.accessor("office_name", {
            header: () => <div className="th-text">Office</div>,
            cell: (info) => (
                <div className="text-sm">{info.getValue() || "--"}</div>
            ),
        }),
        // Conditionally include the correct column
        ...(report === "installation_summary"
            ? [
                columnHelper.accessor("installations", {
                    header: () => <div className="th-text">Installation Order</div>,
                    cell: (info) => (
                        <div className="text-sm">{info.getValue() || "--"}</div>
                    ),
                }),
            ]
            : [
                columnHelper.accessor("sales_orders", {
                    header: () => <div className="th-text">Sales Order</div>,
                    cell: (info) => (
                        <div className="text-sm">{info.getValue() || "--"}</div>
                    ),
                }),
            ]),
        // columnHelper.accessor("payment_method", {
        //     header: () => <div className="th-text">Payment Method</div>,
        //     cell: (info) => (
        //         <div className="text-sm">{info.getValue() || "--"}</div>
        //     ),
        // }),
        columnHelper.accessor("total_billed", {
            header: () => <div className="th-text">Total Billed</div>,
            cell: (info) =>
                <div className="fw-medium text-sm">
                    {typeof info.getValue() === "number" ? info.getValue().toFixed(2) : "--"}
                </div>
        }),
        columnHelper.accessor("paid", {
            header: () => <div className="th-text">Paid</div>,
            cell: (info) =>
                <div className="text-sm" style={{ color: "#00a63e" }}>
                    {typeof info.getValue() === "number" ? `$${info.getValue().toFixed(2)}` : "--"}
                </div>
        }),
        columnHelper.accessor("balance", {
            header: () => <div className="th-text">Balance</div>,
            cell: (info) => {
                const overPaid = info.row.original.over_paid;
                const balanceValue = Number(info.getValue() ?? 0).toFixed(2);
                return (
                    <div className="text-sm fw-medium" style={{ color: overPaid ? "#00a63e" : "#e7000b" }}>
                        {typeof info.getValue() === "number" ? (overPaid ? `+$${balanceValue}` : `-$${balanceValue}`) : "--"}
                    </div>
                );
            }
        }),
    ], [pagination, columnHelper, report]);



    const table = useReactTable({
        data: data || [],
        columns,
        pageCount: Math.ceil(rowCount / pagination.pageSize),
        state: { pagination },
        manualPagination: true,
        getCoreRowModel: getCoreRowModel(),
        onPaginationChange: setPagination,
    });

    useEffect(() => {
        if (topRef.current) {
            topRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
    }, [pagination.pageIndex]);


    return (
        <div className="content" ref={topRef}>
            {
                <style>
                    {`
                        .css-1nmdiq5-menu {
                            z-index: 9999 !important;
                        }
                    `}
                </style>
            }
            <div className="mb-4">
                {
                    report === "installation_summary" ? (
                        <div>
                            <h4 className="mb-1 po-title">Installation Summary</h4>
                            <p className="po-subtitle">Track installer performance and job completion metrics</p>
                        </div>
                    ) : (
                        <div>
                            <h4 className="mb-1 po-title">Sales Order Summary</h4>
                            <p className="po-subtitle">Track sales orders and billing summary across agents and offices</p>
                        </div>
                    )
                }
            </div>
            <div className="row g-3 mb-4">
                <div className="col-lg-3">
                    <StatCard
                        label={`${report === "installation_summary" ? "Total Installations" : "Total Orders"}`}
                        value={report === "installation_summary" ? cards?.total_installations ?? 0 : cards?.total_orders ?? 0}
                        money={false}
                        accent="blue"
                        icon="ri-settings-3-line"
                    />
                </div>

                <div className="col-lg-3">
                    <StatCard
                        label={`${report === "installation_summary" ? "Installations Completed" : "Average Order Value"}`}
                        value={report === "installation_summary" ? (cards?.installations_completed ?? 0) : (cards?.avg_order_value?? 0)}
                        money={report !== "installation_summary"}
                        accent="green"
                        icon="bi-graph-up-arrow"
                    />
                </div>

                <div className="col-lg-3">
                    <StatCard
                        label={`${report === "installation_summary" ? "% Paid" : "Total Paid"}`}
                        value={report === "installation_summary" ?(cards?.percent_paid ?? 0) :(cards?.amount_paid ?? 0)}
                        money={report !== "installation_summary"}
                        accent="green"
                        icon="bi-currency-dollar"
                    />
                </div>

                <div className="col-lg-3">
                    <StatCard
                        label={`${report === "installation_summary" ? "% Pending" : "Total Balance"}`}
                        value={report === "installation_summary" ? (cards?.percent_pending ?? 0) : (cards?.outstanding_balance ?? 0)}
                        money={report !== "installation_summary"}
                        accent="red"
                        icon="bi-exclamation-octagon"
                    />
                </div>
            </div>

            <div className="po-header mb-4 shadow-border-datatables">
                {/* TOP ROW */}
                <div className="po-top d-flex justify-content-between align-items-start align-items-md-center flex-wrap gap-2">
                    <div className="po-title-wrap me-2">
                    </div>

                    {/* tools block (search + buttons) */}
                    <div className="po-tools d-flex align-items-center flex-wrap gap-2 ms-auto">
                        {/* Search */}
                        <div className="input-group po-search ">
                            <span className="input-group-text">
                                <i className="ri-search-line" />
                            </span>
                            <input
                                type="text"
                                id="searchInput"
                                name="searchInput"
                                className="form-control"
                                placeholder="Search by Agent, Office, Brokerage Firm ..."
                                value={searchInput}
                                onChange={(e) => setSearchInput(e.target.value)}
                                onKeyDown={(e) => { if (e.key === "Enter") setGlobalFilter(searchInput); }}
                            />
                        </div>

                        {/* Filters toggle */}
                        <button
                            type="button"
                            className={` po-filter-btn d-inline-flex align-items-center ${filtersOpen ? "active" : ""}`}
                            onClick={() => setFiltersOpen(v => !v)}
                            aria-expanded={filtersOpen}
                            aria-controls="incoming-filters"
                        >
                            <i className="ri-filter-3-line me-2" />
                            <span>Filters</span>
                            <i className={`ri-arrow-down-s-line ms-1 ${filtersOpen ? "rotate-180" : ""}`} />
                        </button>
                    </div>
                </div>

                <div id="incoming-filters" className={`po-filters ${filtersOpen ? "show" : ""}`}>
                    <div className="po-filters-bar d-flex"> {/* <-- flex wrapper */}
                        <div className="row g-2 g-md-3 align-items-end justify-content-md-end ms-md-auto w-100 w-md-auto">

                            {/* Date Range */}
                            <div className="col-lg-2">
                                <label className="form-label mb-1 label-1">Date Range</label>
                                <Select
                                    options={DATE_OPTIONS}
                                    value={datePreset}
                                    onChange={(val) => setDatePreset(val!)}
                                    components={{ DropdownIndicator }}
                                    styles={customSelectStyles}
                                    classNamePrefix="rs"
                                />
                            </div>
                            {datePreset.value === "custom" && (
                                <>
                                    <div className="col-lg-2">
                                        <label className="form-label mb-1 label-1">From</label>
                                        <input
                                            type="date"
                                            className="form-control"
                                            value={customFrom}
                                            onChange={(e) => setCustomFrom(e.target.value)}
                                            min={dateRangeLimits.minDate}
                                            max={dateRangeLimits.maxDate}
                                        />
                                    </div>
                                    <div className="col-lg-2">
                                        <label className="form-label mb-1 label-1">To</label>
                                        <input
                                            type="date"
                                            className="form-control"
                                            value={customTo}
                                            onChange={(e) => setCustomTo(e.target.value)}
                                            min={dateRangeLimits.minDate}
                                            max={dateRangeLimits.maxDate}
                                        />
                                    </div>
                                </>
                            )}
                            {/* Office */}
                            <div className="col-lg-2">
                                <label className="form-label mb-1 label-1">Office</label>
                                {/* TODO: Use async Select to fetch office list */}
                                <Select
                                    options={officeOptions}
                                    value={selectedOffice}
                                    onChange={val => setSelectedOffice(val ?? null)}
                                    placeholder="Select Office"
                                    components={{ DropdownIndicator }}
                                    styles={customSelectStyles}
                                    classNamePrefix="rs"
                                // isClearable
                                />
                            </div>
                            {/* Agent */}
                            <div className="col-lg-2">
                                <label className="form-label mb-1 label-1">Agent</label>
                                <AsyncPaginate
                                    value={selectedAgent}
                                    loadOptions={loadAgentOptions}
                                    onChange={(val: any) => setSelectedAgent(val ?? null)}
                                    additional={{ page: 1 }}
                                    placeholder="Select Agent"
                                    isDisabled={!brokerageId && !selectedOffice}
                                    debounceTimeout={300}
                                    defaultOptions
                                    components={{ DropdownIndicator }}
                                    styles={customSelectStyles}
                                    classNamePrefix="rs"
                                />
                            </div>
                            {/* Supplier */}
                            <div className="col-lg-2">
                                <label className="form-label mb-1 label-1">Supplier</label>
                                <Select
                                    options={supplierOptions} value={supplier}
                                    onChange={val => setSupplier(val ?? null)}
                                    placeholder="Select Supplier"
                                    components={{ DropdownIndicator }}
                                    styles={customSelectStyles}
                                    classNamePrefix="rs"
                                // isClearable
                                />
                            </div>

                            {/* Payment Status */}
                            <div className="col-lg-2">
                                <label className="form-label mb-1 label-1">Payment Status</label>
                                <Select
                                    options={PAYMENT_STATUS_OPTIONS}
                                    value={paymentStatus}
                                    onChange={(val) => setPaymentStatus(val!)}
                                    components={{ DropdownIndicator }}
                                    styles={customSelectStyles}
                                    classNamePrefix="rs"
                                />
                            </div>

                            <div className="col-lg-auto d-flex align-items-end gap-2">
                                <button className="btn btn-primary table-addbutton br-8" onClick={handleGenerate}>
                                    Apply Filter
                                </button>
                                <button className="btn back-btn surface-btn fw-bold fs-16" onClick={clearFilters}>
                                    Clear
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* Table */}
            <div className="card overflow-hidden production-card box-shadow-datatables shadow-border-datatables">
                <div className="align-items-center bg-white border-bottom d-flex justify-content-between px-4 py-4">
                    <div className="d-flex align-items-center">
                        <i
                            className="ri-file-text-line me-2"
                            aria-hidden="true"
                            style={{ fontSize: 18, color: "#2563eb", lineHeight: 1 }}
                        />
                        <h5 className="mb-0">{report === "installation_summary" ? "Installation Data" : "Sales Order Data"}</h5>
                    </div>
                </div>
                <hr className="mb-2" style={{ borderTop: "none" }} />


                {/* DESKTOP/TABLET */}
                <div className="rdt-desktop">
                    <div
                        className="production-scroll"
                        style={{ "--rdt-min": "1200px" } as React.CSSProperties}
                    >
                        <table className="table production-table align-middle mb-0 table-1">
                            <DesktopTableParts table={table} />
                        </table>
                    </div>
                </div>

                {/* MOBILE (≤ 1021px) */}
                <div className="rdt-mobile">
                    <MobileCardsOnly
                        table={table}
                        idColId="order_link"
                        titleColId="product_name"
                        actionColId="actions"
                    />
                </div>

            </div>
            <div className="mt-4 mb-4">
                <TablePaginationControls table={table} />
            </div>
        </div>
    )
}
